import { useEffect, useRef, useState } from "react";

import {
  GqlSectionItem,
  isSectionItemField,
  isSectionItemGroup,
} from "../types";

export const useLastActiveIds = (sectionItems: GqlSectionItem[]) => {
  const [lastActiveIds, setLastActiveIds] = useState<string[]>([]);
  const previousUrisRef = useRef<Set<string> | undefined>(undefined);
  const previousSectionUriRef = useRef<string | undefined>(undefined);
  const timerRef = useRef<NodeJS.Timeout | undefined>(undefined);
  const lastActiveResetTimeInMs = 1000;

  const getAllUris = (items: GqlSectionItem[]): string[] =>
    items.flatMap((item) => {
      if (isSectionItemField(item)) {
        return [item.uri];
      }
      if (isSectionItemGroup(item)) {
        return [item.uri, ...item.fields.map((field) => field.uri)];
      }
      return [];
    });

  const setLastActiveIdsWithTimer = (ids: string[]) => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
    }

    setLastActiveIds(ids);
    if (ids.length > 0) {
      timerRef.current = setTimeout(
        () => setLastActiveIds([]),
        lastActiveResetTimeInMs,
      );
    }
  };

  useEffect(() => {
    const currentUris = new Set(getAllUris(sectionItems));
    const newUris =
      previousSectionUriRef.current === sectionItems[0]?.sectionUri
        ? [...currentUris].filter((uri) => !previousUrisRef.current!.has(uri))
        : [];

    if (newUris.length > 0) {
      setLastActiveIdsWithTimer(newUris);
    }

    previousSectionUriRef.current = sectionItems[0]?.sectionUri;
    previousUrisRef.current = currentUris;
  }, [sectionItems]);

  useEffect(
    () => () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
    },
    [],
  );

  return { lastActiveIds, setLastActiveIds: setLastActiveIdsWithTimer };
};
